import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import type { SnackbarContextType } from '@/contexts/SnackbarContext.types';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';
import { handleSnackbarError } from '@/shared/utils/errorHandler';

type UserDistributorGroup = components['schemas']['UserDistributorGroup'];

type DistributorSnapshotRequest = components['schemas']['AddDistributorGroupsToUserEndpoint_DistributorSnapshotRequest'];

type DistributorsAutocompleteResponse = components['schemas']['GetDistributorsAutocompleteEndpoint_DistributorsAutocompleteResponse'];

type OperationType = 'add' | 'remove' | null;

export interface UseManageUserDistributorGroupsReturn {
  addGroups: (userId: string, groupNames: string[]) => Promise<UserDistributorGroup[] | null>;
  removeGroup: (groupId: number, userId: string, role: string) => Promise<boolean>;
  loading: boolean;
  operationType: OperationType;
  error: string | null;
}

interface UseManageUserDistributorGroupsConfig {
  snackbar: SnackbarContextType;
}

export function useManageUserDistributorGroups({ snackbar }: UseManageUserDistributorGroupsConfig): UseManageUserDistributorGroupsReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  const getDistributorGroupInfo = useCallback(
    async (groupName: string): Promise<DistributorSnapshotRequest | null> => {
      try {
        const client = createApiClient(getAccessToken);
        const response = await client.GET('/api/organizations/{organizationId}/distributor-groups/autocomplete', {
          params: {
            path: { organizationId },
            query: { Filter: groupName },
          },
        });
        const data = response.data as DistributorsAutocompleteResponse[] | undefined;

        if (!data || data.length === 0) {
          return null;
        }

        const group = data.find((g) => (g as DistributorsAutocompleteResponse).distributorName === groupName);

        if (!group) {
          return null;
        }

        const distributorGroup = group as DistributorsAutocompleteResponse;
        if (!distributorGroup.systemId || !distributorGroup.distributorName) {
          return null;
        }

        return {
          distributorGroupId: distributorGroup.systemId,
          distributorName: distributorGroup.distributorName,
          distributorId: distributorGroup.distributorId ?? null,
          country: distributorGroup.country ?? null,
          state: distributorGroup.state ?? null,
        } as DistributorSnapshotRequest;
      } catch (err: unknown) {
        console.error('Error fetching distributor group info:', err);
        return null;
      }
    },
    [organizationId, getAccessToken],
  );

  const addGroupsMutation = useMutation({
    mutationFn: async ({ userId, groupNames }: { userId: string; groupNames: string[] }): Promise<UserDistributorGroup[]> => {
      const groupSnapshots: DistributorSnapshotRequest[] = [];
      for (const groupName of groupNames) {
        const groupInfo = await getDistributorGroupInfo(groupName);
        if (groupInfo) {
          groupSnapshots.push(groupInfo);
        }
      }

      if (groupSnapshots.length === 0) {
        throw new Error('No valid distributor groups found');
      }

      const client = createApiClient(getAccessToken);

      const requestBody = {
        userId,
        distributors: groupSnapshots,
      };
      const result = await client.POST('/api/organizations/{organizationId}/users/distributor-groups', {
        params: { path: { organizationId } },
        body: requestBody,
      });
      return Array.isArray(result.data) ? (result.data as UserDistributorGroup[]) : [];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['userById', organizationId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['user-distributor-group-membership', organizationId],
        exact: false,
      });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const addGroups = useCallback(
    async (userId: string, groupNames: string[]): Promise<UserDistributorGroup[] | null> => {
      try {
        const result = await addGroupsMutation.mutateAsync({ userId, groupNames });
        return result;
      } catch {
        return null;
      }
    },
    [addGroupsMutation],
  );

  const removeGroupMutation = useMutation({
    mutationFn: async ({ groupId, userId, role }: { groupId: number; userId: string; role: string }): Promise<boolean> => {
      const client = createApiClient(getAccessToken);

      const { response } = await client.DELETE('/api/organizations/{organizationId}/distributor-groups/{distributorGroupId}/users', {
        params: {
          path: { organizationId, distributorGroupId: groupId },
          query: { UserId: userId, Role: role },
        },
      });
      return response.status === 204 || response.ok;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['userById', organizationId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['user-distributor-group-membership', organizationId],
        exact: false,
      });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const removeGroup = useCallback(
    async (groupId: number, userId: string, role: string): Promise<boolean> => {
      try {
        return await removeGroupMutation.mutateAsync({ groupId, userId, role });
      } catch {
        return false;
      }
    },
    [removeGroupMutation],
  );

  const loading = addGroupsMutation.isPending || removeGroupMutation.isPending;
  const operationType: OperationType = addGroupsMutation.isPending ? 'add' : removeGroupMutation.isPending ? 'remove' : null;
  const error =
    addGroupsMutation.error instanceof Error
      ? addGroupsMutation.error.message
      : removeGroupMutation.error instanceof Error
        ? removeGroupMutation.error.message
        : null;

  return {
    addGroups,
    removeGroup,
    loading,
    operationType,
    error,
  };
}
